import { useEffect, useState } from 'preact/hooks';
import { useRevalidate } from '../state/useRevalidate';
import type { Invalidation } from '../state/events';
import { type Session, describeError } from '../api/client';
import {
  monthDatedTasks,
  monthEvents,
  monthFrom,
  monthLabel,
  monthOf,
  monthTasks,
  shiftMonth,
  type MonthRange,
} from '../api/month';
import { entryRow } from '../api/adapter';
import { dayLabel, dayNumber } from '../api/week';
import type { WireEntry, WireEventRange } from '../api/wire';

import { SegmentedNav } from '../components/a11y/SegmentedNav';
import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { EntryLine } from '../components/composed/EntryLine';
import { Pressable } from '../components/a11y/Pressable';

/** At most this many titles on a calendar day row before it collapses to a
 *  count. Hidden content is never silent. */
const DAY_TITLE_CAP = 2;

/** The Monthly Log: a calendar face and a task inbox.
 *
 *  They are two faces rather than one list because they read from different
 *  places. Month-scoped tasks live on `active_month` and are deliberately
 *  excluded from date-range queries, so a task is in exactly one face — the
 *  inbox if it has no date, the calendar if it does.
 */
export function Month({
  session,
  journalDate,
  startMonth,
  refresh,
  onFaceChange,
  onOpenDay,
  onOpenActions,
  invalidation,
}: {
  session: Session;
  journalDate: string;
  /** Open on this month rather than the journal's current one. The Future Log
   *  sends a far month here, and without it the screen showed August while the
   *  row that opened it said November. */
  startMonth?: string;
  /** Bumped when something on this device changed the journal. A signal, not a
   *  key — see the note in `Today`. */
  refresh?: number;

  /** Which face is showing, and which month. The capture bar is only offered on
   *  the inbox — the calendar face is read-only, as it is in the TUI — and it has
   *  to aim at the month being viewed rather than the journal's own. */
  onFaceChange?: (face: 'CALENDAR' | 'INBOX', month: string) => void;
  onOpenDay: (date: string) => void;
  onOpenActions: (entry: WireEntry) => void;
  /** The latest recorded change. Both faces live in one month, so either an
   *  invalidated month or an invalidated date inside it reloads this. */
  invalidation?: Invalidation | null;
}) {
  const [range, setRange] = useState<MonthRange>(() =>
    startMonth ? monthFrom(startMonth) : monthOf(journalDate),
  );
  const revalidate = useRevalidate(invalidation ?? null, {
    kind: 'month',
    month: range.month,
  });
  const [face, setFace] = useState<'CALENDAR' | 'INBOX'>('CALENDAR');
  useEffect(() => {
    onFaceChange?.(face, range.month);
  }, [onFaceChange, face, range.month]);
  const [events, setEvents] = useState<WireEventRange | null>(null);
  const [dated, setDated] = useState<WireEntry[]>([]);
  const [inbox, setInbox] = useState<WireEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const [ev, dt, ib] = await Promise.all([
          monthEvents(session, range),
          monthDatedTasks(session, range),
          monthTasks(session, range.month),
        ]);
        if (cancelled) return;
        setEvents(ev);
        setDated(dt);
        setInbox(ib);
      } catch (caught) {
        if (!cancelled) {
          setError(
            describeError(caught),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, range, revalidate, refresh]);

  const byDate = new Map<string, { events: WireEventRange['events']; tasks: WireEntry[] }>();
  for (const day of range.days) byDate.set(day, { events: [], tasks: [] });
  for (const event of events?.events ?? []) {
    const key = (event.start ?? '').slice(0, 10);
    byDate.get(key)?.events.push(event);
  }
  for (const task of dated) {
    if (task.active_date) byDate.get(task.active_date)?.tasks.push(task);
  }

  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' }}>
      <header style={{ paddingBottom: 'var(--bujo-space-2)' }}>
        <div style={eyebrow}>BUJO / MONTHLY LOG</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--bujo-space-3)', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)', color: 'var(--bujo-accent)' }}>
            {monthLabel(range.month)}
          </h1>
          {/* Month stepping sits beside the title rather than on its own line:
              it is an adjustment to what the title says, not a section. */}
          <nav aria-label="Change month" style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--bujo-space-2)' }}>
            <button type="button" onClick={() => setRange(monthFrom(shiftMonth(range.month, -1)))} style={nav}>
              ◀ {monthLabel(shiftMonth(range.month, -1)).slice(0, 3)}
            </button>
            <span style={{ ...nav, cursor: 'default' }}>·</span>
            <button type="button" onClick={() => setRange(monthFrom(shiftMonth(range.month, 1)))} style={nav}>
              {monthLabel(shiftMonth(range.month, 1)).slice(0, 3)} ▶
            </button>
          </nav>
        </div>
      </header>

      {/* Two faces, not two pages: the design's spread never stacks vertically. */}
      <SegmentedNav
        variant="underline"
        label="Monthly log face"
        active={face}
        onSelect={setFace}
        options={[
          { value: 'CALENDAR', label: 'CALENDAR' },
          { value: 'INBOX', label: 'TASKS', count: inbox?.length ?? 0 },
        ]}
      />

      {error ? (
        <p style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)', color: 'var(--bujo-state-error)' }}>
          ! {error}
        </p>
      ) : null}

      {face === 'CALENDAR' ? (
        <section>
          <div style={faceNote}>
            {events ? 'READ-ONLY · CHANGE EVENTS WHERE THEY LIVE' : 'NO CALENDAR CONNECTED'}
          </div>
          {range.days.map((date) => {
            const day = byDate.get(date);
            const titles = [
              ...(day?.events ?? []).map((e) => e.title),
              ...(day?.tasks ?? []).map((t) => t.content),
            ];
            const shown = titles.slice(0, DAY_TITLE_CAP);
            const hidden = titles.length - shown.length;
            const isToday = date === journalDate;
            return (
              <Pressable
                key={date}
                label={`${dayLabel(date)} ${dayNumber(date)}${titles.length ? `: ${titles.join(', ')}` : ', nothing'} — open this day`}
                onPress={() => onOpenDay(date)}
              >
                <div
                  style={{
                    ...dayRow,
                    background: isToday ? 'var(--bujo-select-ground)' : 'transparent',
                    boxShadow: isToday
                      ? 'inset var(--bujo-select-edge-width) 0 0 var(--bujo-select-edge)'
                      : 'none',
                    paddingLeft: isToday ? 'var(--bujo-space-2)' : 0,
                  }}
                >
                  {/* Number and weekday on one line, reading left to right like
                      a date. Stacking them right-aligned made the column a
                      table header rather than a date. */}
                  <span style={{ ...dateCell, color: isToday ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)' }}>
                    {isToday ? '▸ ' : ''}
                    {Number(dayNumber(date))} {dayLabel(date)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {/* Joined with a middot on one line, wrapping if needed. An
                        empty day carries nothing at all: a placeholder glyph
                        across thirty-one rows is noise, not information. */}
                    {shown.length > 0 ? (
                      <span
                        style={{
                          font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1.35 var(--bujo-font-mono)',
                          color: 'var(--bujo-text-primary)',
                          textWrap: 'pretty',
                        }}
                      >
                        {shown.join(' · ')}
                        {hidden > 0 ? (
                          <span style={{ color: 'var(--bujo-accent)' }}> · +{hidden} more</span>
                        ) : null}
                      </span>
                    ) : null}
                  </span>
                </div>
              </Pressable>
            );
          })}
        </section>
      ) : (
        <section>
          <div style={faceNote}>NOT YET SCHEDULED ONTO A DAY</div>
          {inbox === null ? null : inbox.length === 0 ? (
            <EmptyState
              line="Nothing in the inbox."
              detail="Month tasks are the ones without a day yet."
            />
          ) : (
            inbox.map((task) => (
              <EntryLine
                key={task.id}
                entry={entryRow(task)}
                onPress={() => onOpenActions(task)}
              />
            ))
          )}
        </section>
      )}
    </main>
  );
}

const faceNote = {
  padding: 'var(--bujo-space-3) 0 var(--bujo-space-2)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-text-meta)',
};

const dayRow = {
  display: 'flex',
  gap: 'var(--bujo-space-3)',
  alignItems: 'baseline',
  minHeight: 'var(--bujo-row-min)',
  padding: '4px 0',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};

const dateCell = {
  width: 62,
  flex: 'none' as const,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.35 var(--bujo-font-mono)',
};

const nav = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};

const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
